import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper to load fonts
const loadFont = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        const buffer = await response.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    } catch (error) {
        console.error("Error loading font:", url, error);
        return null;
    }
};

export const generateInvoice = async (order, userDetails, action = 'save') => {
    try {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // --- Font Loading ---
        // Using standard fonts for reliability, but keep structure for custom if needed
        // Assuming strictly standard fonts to save load time/complexity for now as per design "sans"
        // But if we want consistent brand font:
        const fontRegularUrl = "https://raw.githubusercontent.com/google/fonts/main/ofl/chakrapetch/ChakraPetch-Regular.ttf";
        const fontBoldUrl = "https://raw.githubusercontent.com/google/fonts/main/ofl/chakrapetch/ChakraPetch-Bold.ttf";

        const [fontRegularBase64, fontBoldBase64] = await Promise.all([
            loadFont(fontRegularUrl),
            loadFont(fontBoldUrl)
        ]);

        let customFontLoaded = false;
        if (fontRegularBase64 && fontBoldBase64) {
            doc.addFileToVFS("ChakraPetch-Regular.ttf", fontRegularBase64);
            doc.addFont("ChakraPetch-Regular.ttf", "ChakraPetch", "normal");
            doc.addFileToVFS("ChakraPetch-Bold.ttf", fontBoldBase64);
            doc.addFont("ChakraPetch-Bold.ttf", "ChakraPetch", "bold");
            customFontLoaded = true;
        }

        const setFont = (type = 'normal', size = 10, color = [255, 255, 255]) => {
            if (customFontLoaded) {
                // Map 'bold' to 'bold' (which we named "ChakraPetch" style "bold")
                // Map 'normal' to 'normal'
                doc.setFont("ChakraPetch", type === 'bold' ? 'bold' : 'normal');
            } else {
                // Fallback only if totally failed, but user asked to NOT show helvetica.
                // We'll try to stick to the custom font if at all possible or just keep this fail-safe
                // but since the request is "not need to show me helvetica", we prioritize Chakra.
                doc.setFont("ChakraPetch", type === 'bold' ? 'bold' : 'normal');
            }
            doc.setFontSize(size);
            doc.setTextColor(...color);
        };

        // --- 1. Background (Dark #0f0f0f) ---
        doc.setFillColor(15, 15, 15);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // --- 2. Decorative Blobs (Simulated with simple shapes/gradients or just accents) ---
        doc.saveGraphicsState();
        // Top Right Purple
        doc.setFillColor(147, 51, 234); // Purple-600
        doc.setGState(new doc.GState({ opacity: 0.05 }));
        doc.circle(pageWidth, 0, 80, 'F');
        // Bottom Left Pink
        doc.setFillColor(219, 39, 119); // Pink-600
        doc.circle(0, pageHeight, 60, 'F');
        doc.restoreGraphicsState(); // Reset opacity

        // --- 3. Watermark ---
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.03 }));
        setFont('bold', 140, [255, 255, 255]);
        const centerX = pageWidth / 2;
        const centerY = pageHeight / 2;
        const offset = 30;
        doc.text("YOYO", centerX + offset, centerY + offset, {
            align: "center",
            angle: 45
        });
        doc.restoreGraphicsState();

        const margin = 15;
        let yPos = 20;

        // --- 4. Header ---
        // Brand - Gradient Text
        setFont('bold', 25, [255, 255, 255]);
        const brandText = "YOYO GAMES";
        const startColor = [192, 132, 252]; // Purple 400
        const endColor = [219, 39, 119];   // Pink 600

        let currentX = margin;
        const chars = brandText.split("");

        chars.forEach((char, index) => {
            const t = index / Math.max(1, chars.length - 1);
            const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * t);
            const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * t);
            const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * t);

            doc.setTextColor(r, g, b);
            doc.text(char, currentX, yPos);
            currentX += doc.getTextWidth(char) + 0.5; // Slight manual tracking for better readability
        });

        yPos += 6;
        setFont('normal', 9, [156, 163, 175]); // Gray 400
        doc.text("Powered by VS Infotech.", margin + 1, yPos);


        // Status Badge (Top Right)
        const statusText = order.status ? order.status.toUpperCase() : "UNKNOWN";
        const isPaid = order.status === 'paid';

        // Colors
        const statusTextColor = isPaid ? [52, 211, 153] : [250, 204, 21]; // Emerald-400 : Yellow-400
        const statusBgColor = isPaid ? [6, 78, 59] : [66, 32, 6];     // Emerald-900 : Yellow-900 (Darker BG)


        doc.setFontSize(10);
        doc.setFont("ChakraPetch", "bold");

        // Layout
        const textW = doc.getTextWidth(statusText);
        const dotRadius = 1.0;
        const dotGap = 3;
        const paddingX = 4;
        const badgeH = 9;
        const badgeW = textW + (dotRadius * 2) + dotGap + (paddingX * 2);

        const badgeX = pageWidth - margin - badgeW;
        const badgeY = 15;

        // Draw Badge Background (No Border)
        doc.setFillColor(...statusBgColor);

        // Use lower opacity for fill to simulate "bg-emerald-500/10" effect if supported cleanly,
        // but solid dark color is safer for consistency. We'll stick to the solid dark fill 
        // but use a thinner line width for the border to match "border-emerald-500/20" vibe?
        // Actually, jsPDF 'FD' draws filled then stroked.
        // No border
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 4.5, 4.5, 'F');

        // Draw Dot
        const dotCx = badgeX + paddingX + dotRadius;
        const dotCy = badgeY + (badgeH / 2);

        doc.setFillColor(...statusTextColor); // Dot uses text color (bright)
        doc.circle(dotCx, dotCy, dotRadius, 'F');

        // Draw Text
        doc.setTextColor(...statusTextColor);
        const textX = dotCx + dotRadius + dotGap;
        const textY = badgeY + (badgeH / 2);
        doc.text(statusText, textX, textY, { baseline: 'middle' });

        // --- 5. Info Grid ---
        yPos += 20;

        // Left: Bill To
        setFont('bold', 9, [107, 114, 128]); // Gray 500
        // Text shifted right to make room for dot on left
        doc.text("BILLED TO", margin + 5, yPos);

        // Small decorative dot (Left Side)
        doc.setFillColor(168, 85, 247); // Purple
        doc.circle(margin + 1, yPos - 1, 1, 'F');

        yPos += 8;
        setFont('bold', 14, [255, 255, 255]);
        doc.text(String(userDetails.name || "Customer"), margin, yPos);
        yPos += 6;
        setFont('normal', 10, [156, 163, 175]);
        doc.text(String(userDetails.email || ""), margin, yPos);

        // Right: Invoice Details
        const rightColX = pageWidth - margin;
        let rightY = yPos - 14;

        setFont('bold', 9, [107, 114, 128]);
        // Text shifted slightly left to make room for dot on right
        doc.text("INVOICE DETAILS", rightColX - 5, rightY, { align: "right" });
        // Dot (Right Side)
        doc.setFillColor(236, 72, 153); // Pink
        doc.circle(rightColX - 1, rightY - 1, 1, 'F');

        rightY += 8;
        // Invoice No
        setFont('normal', 10, [156, 163, 175]);
        doc.text("Invoice No.", rightColX - 30, rightY, { align: "right" });
        setFont('bold', 10, [255, 255, 255]);
        doc.text(`#${String(order._id).slice(-8).toUpperCase()}`, rightColX, rightY, { align: "right" });

        rightY += 6;
        // Date
        setFont('normal', 10, [156, 163, 175]);
        doc.text("Date Issued", rightColX - 30, rightY, { align: "right" });
        setFont('normal', 10, [255, 255, 255]);
        doc.text(new Date(order.createdAt).toLocaleDateString(), rightColX, rightY, { align: "right" });

        // Divider
        yPos += 15;
        // Removed line/border as requested "remove border from PDF"
        // keeping logic flow correct

        // --- 6. Items List (AutoTable but Custom Look) ---
        yPos += 10;
        setFont('bold', 9, [107, 114, 128]);
        doc.text("PURCHASED ITEMS", margin, yPos);

        const tableY = yPos + 5;

        // Prepare data - REMOVED IMAGE
        const tableBody = order.items.map(item => [
            {
                content: item.game?.title || "Unknown Game",
                platform: item.platform || "PC",
                price: item.price
            }
        ]);

        autoTable(doc, {
            startY: tableY,
            body: tableBody,
            theme: 'plain',
            rowPageBreak: 'avoid',
            margin: { left: margin, right: margin },
            bodyStyles: {
                valign: 'middle',
                minCellHeight: 18, // slightly smaller since no image
            },
            columnStyles: {
                0: { cellWidth: 'auto' }
            },
            didDrawCell: (data) => {
                if (data.column.index === 0 && data.cell.section === 'body') {
                    const { doc, cell } = data;
                    const { x, y, width, height } = cell;

                    // 1. Draw Card Background - NO BORDER
                    doc.saveGraphicsState();
                    doc.setGState(new doc.GState({ opacity: 0.03 }));
                    doc.setFillColor(255, 255, 255);
                    doc.roundedRect(x, y + 2, width, height - 4, 3, 3, 'F'); // Fill only
                    doc.restoreGraphicsState();

                    // 2. Content
                    const item = data.cell.raw;
                    const pad = 10; // Increased padding since image is gone

                    // Text Info
                    const textX = x + pad;
                    const textY = y + height / 2;

                    // Title
                    doc.setFontSize(10);
                    doc.setFont("ChakraPetch", "bold");
                    doc.setTextColor(255, 255, 255);
                    doc.text(item.content, textX, textY - 1);

                    // Platform
                    doc.setFontSize(8);
                    doc.setFont("ChakraPetch", "normal");
                    doc.setTextColor(156, 163, 175);
                    doc.text(String(item.platform).toUpperCase(), textX, textY + 4);

                    // Price (Right Aligned)
                    const priceStr = `$${Number(item.price).toFixed(2)}`;
                    doc.setFontSize(10);
                    doc.setFont("ChakraPetch", "bold");
                    doc.setTextColor(255, 255, 255); // White for clean look
                    doc.text(priceStr, x + width - pad, textY + 1, { align: "right" });
                }
            }
        });

        // --- 7. Totals Section ---
        let finalY = doc.lastAutoTable.finalY + 10;

        // Ensure no page break overflow for totals
        if (finalY + 60 > pageHeight) {
            doc.addPage();
            finalY = 20;
        }

        const boxW = 80;
        const boxH = 55;
        const boxX = pageWidth - margin - boxW;

        // Gradient glow or background for totals box
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.02 }));
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(boxX, finalY, boxW, boxH, 4, 4, 'F'); // Fill only
        doc.restoreGraphicsState();

        // Totals Content
        let cy = finalY + 12;
        const lx = boxX + 8;
        const rx = boxX + boxW - 8;

        let subtotal = 0;
        if (order.items) subtotal = order.items.reduce((s, i) => s + (Number(i.price) || 0), 0);
        const tax = subtotal * 0.18;
        const discount = subtotal * 0.05;

        // 1. Subtotal
        doc.setFontSize(9);
        doc.setTextColor(156, 163, 175); // Gray for label
        doc.setFont("ChakraPetch", "normal");
        doc.text("Subtotal", lx, cy);

        doc.setTextColor(255, 255, 255); // White Value
        doc.setFont("ChakraPetch", "bold"); // 'medium' usually maps to bold or normal in jsPDF basic fonts unless added specifically
        doc.text(`$${subtotal.toFixed(2)}`, rx, cy, { align: "right" });
        cy += 8;

        // 2. Tax (18%)
        doc.setTextColor(156, 163, 175);
        doc.setFont("ChakraPetch", "normal");
        doc.text("Tax", lx, cy);
        // Badge 18%
        const taxBadgeW = doc.getTextWidth("18%") + 6;
        const totalsBadgeH = 5;
        const taxLabelW = doc.getTextWidth("Tax");
        const taxBadgeX = lx + taxLabelW + 4;
        const taxBadgeY = cy - 4; // Align with text baseline visually

        doc.setFillColor(50, 50, 50); // Dark gray badge
        doc.roundedRect(taxBadgeX, taxBadgeY, taxBadgeW, totalsBadgeH, 1.5, 1.5, 'F');
        doc.setTextColor(200, 200, 200);
        doc.setFontSize(7);
        // Perfect centering
        doc.text("18%", taxBadgeX + (taxBadgeW / 2), taxBadgeY + (totalsBadgeH / 2), { align: "center", baseline: "middle" });

        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255); // White Value
        doc.setFont("ChakraPetch", "bold");
        doc.text(`+$${tax.toFixed(2)}`, rx, cy, { align: "right" });
        cy += 8;

        // 3. Discount (5%)
        doc.setTextColor(156, 163, 175);
        doc.setFontSize(9);
        doc.setFont("ChakraPetch", "normal");
        doc.text("Discount", lx, cy);

        // Badge 5%
        const discBadgeW = doc.getTextWidth("5%") + 6;
        const discLabelW = doc.getTextWidth("Discount");
        const discBadgeX = lx + discLabelW + 4;
        const discBadgeY = cy - 4; // Same vertical alignment logic

        doc.setFillColor(6, 78, 59); // Green badge bg
        doc.roundedRect(discBadgeX, discBadgeY, discBadgeW, totalsBadgeH, 1.5, 1.5, 'F');
        doc.setTextColor(52, 211, 153); // Green text
        doc.setFontSize(7);
        // Perfect centering
        doc.text("5%", discBadgeX + (discBadgeW / 2), discBadgeY + (totalsBadgeH / 2), { align: "center", baseline: "middle" });

        doc.setFontSize(9);
        // Green Value
        doc.setTextColor(52, 211, 153);
        doc.setFont("ChakraPetch", "bold");
        doc.text(`-$${discount.toFixed(2)}`, rx, cy, { align: "right" });

        // Divider
        cy += 4;
        doc.saveGraphicsState();
        doc.setDrawColor(255, 255, 255);
        doc.setGState(new doc.GState({ opacity: 0.2 }));
        doc.line(lx, cy, rx, cy);
        doc.restoreGraphicsState();
        cy += 10;

        // Grand Total
        doc.setFontSize(10);
        doc.setTextColor(156, 163, 175);
        doc.setFont("ChakraPetch", "normal");
        doc.text("Total", lx, cy - 3);

        doc.setFontSize(18);
        doc.setTextColor(255, 255, 255);
        doc.setFont("ChakraPetch", "bold");
        doc.text(`$${order.amount}`, rx, cy, { align: "right" });

        // Hint text below total
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text("Include of all taxes", rx, cy + 5, { align: "right" });


        // --- 8. Footer ---
        const footerY = pageHeight - 12;
        doc.saveGraphicsState();
        doc.setDrawColor(255, 255, 255);
        doc.setGState(new doc.GState({ opacity: 0.1 }));
        doc.line(margin, footerY - 8, pageWidth - margin, footerY - 8);
        doc.restoreGraphicsState();

        setFont('normal', 8, [107, 114, 128]);
        doc.text("Thank you for shopping with YOYO!", margin, footerY);
        doc.text(`Generated: ${new Date().toLocaleTimeString()} • ${new Date().toLocaleDateString()}`, pageWidth - margin, footerY, { align: "right" });

        // --- Save/Open ---
        if (action === 'open') {
            window.open(doc.output('bloburl'), '_blank');
        } else if (action === 'save') {
            doc.save(`yoyo_Invoice_${order._id.slice(-6)}.pdf`);
        }

    } catch (err) {
        console.error("GENERATE INVOICE ERROR", err);
        alert("Failed to generate PDF");
    }
};
